'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import globalAxios from 'axios';
import { useAuth } from './AuthProvider';
import { CartResponseDto, CartItemPopulated, PopulatedOrder, Product } from '@/lib/Apis/models';
import { CartApi, WishlistApi } from '@/lib/Apis';
import { getApiConfiguration, getAuthToken } from '@/lib/api-adapter';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useNotification } from './NotificationProvider';
import { useWishlist } from './WishlistProvider';
import {
  readGuestCart,
  writeGuestCart,
  clearGuestCart,
  addToGuestCart,
  updateGuestCartItem,
  removeFromGuestCart,
} from '@/lib/utils/guestCart';

interface CartContextValue {
  cart: CartResponseDto | null;
  isLoading: boolean;
  isFetching: boolean;
  cartError: string | null;
  clearCartError: () => void;
  addToCart: (productId: string, quantity?: number, variantId?: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: (deliveryMethod?: string, shippingRateId?: string, discountId?: string, userAddressId?: string, guestAddress?: Record<string, any>) => Promise<void>;
  reorderFromOrder: (order: PopulatedOrder) => Promise<void>;
  saveForLater: (item: CartItemPopulated) => Promise<void>;
  /** Merge localStorage guest cart into the server cart after login. No-op if guest cart is empty. */
  mergeGuestCart: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}

interface CartProviderProps {
  children: React.ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [cart, setCart] = useState<CartResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [cartError, setCartError] = useState<string | null>(null);
  const clearCartError = useCallback(() => setCartError(null), []);
  const { isAuthenticated } = useAuth();
  const notification = useNotification();
  const { addToWishlist } = useWishlist();

  // Ref to always have the latest cart state in async functions without stale closures
  const cartRef = useRef<CartResponseDto | null>(cart);
  // Prevent older cart reads from overwriting a newer mutation response.
  const cartFetchSequenceRef = useRef(0);
  const cartMutationSequenceRef = useRef(0);
  const activeCartFetchesRef = useRef(0);
  // Cart updates replace the full server cart, so serialize adds to avoid two
  // rapid clicks building payloads from the same stale snapshot.
  const addQueueRef = useRef<Promise<void>>(Promise.resolve());
  // Debounce timers for quantity updates, keyed by productId
  const pendingUpdatesRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  const commitCart = useCallback((nextCart: CartResponseDto | null) => {
    cartRef.current = nextCart;
    setCart(nextCart);
  }, []);

  // ─── Calculate guest cart prices via onspot endpoint ─────────────────────
  const calculateGuestCart = useCallback(async (deliveryMethod?: string, shippingRateId?: string, discountId?: string, guestAddress?: Record<string, any>) => {
    const requestId = ++cartFetchSequenceRef.current;
    const mutationVersion = cartMutationSequenceRef.current;
    activeCartFetchesRef.current += 1;
    setIsFetching(true);
    const guestItems = readGuestCart();
    try {
      if (guestItems.length === 0) {
        if (requestId === cartFetchSequenceRef.current && mutationVersion === cartMutationSequenceRef.current) {
          commitCart(null);
        }
        return;
      }
      const body: any = { items: guestItems };
      if (deliveryMethod) body.deliveryMethod = deliveryMethod;
      if (discountId) body.discountId = discountId;
      if (guestAddress) body.deliveryAddress = guestAddress;
      const response = await new CartApi(AXIOS_CONFIG).handleVisitorCart(
        body,
        undefined, // no userId for guests
        shippingRateId || undefined
      );
      if (
        response.data &&
        requestId === cartFetchSequenceRef.current &&
        mutationVersion === cartMutationSequenceRef.current
      ) {
        commitCart(response.data);
      }
    } catch (error) {
      console.error('Failed to calculate guest cart:', error);
      throw error;
    } finally {
      activeCartFetchesRef.current = Math.max(0, activeCartFetchesRef.current - 1);
      if (activeCartFetchesRef.current === 0) setIsFetching(false);
    }
  }, [commitCart]);

  // ─── Refresh cart (authenticated or guest) ────────────────────────────────
  const refreshCart = useCallback(async (deliveryMethod?: string, shippingRateId?: string, discountId?: string, userAddressId?: string, guestAddress?: Record<string, any>) => {
    if (!isAuthenticated) {
      return calculateGuestCart(deliveryMethod, shippingRateId, discountId, guestAddress);
    }

    const requestId = ++cartFetchSequenceRef.current;
    const mutationVersion = cartMutationSequenceRef.current;
    activeCartFetchesRef.current += 1;
    setIsFetching(true);
    try {
      let response;
      if (discountId) {
        // Use globalAxios directly to avoid the generated SDK spreading `options.params`
        // into the axios config, which causes params to be appended twice to the URL.
        const params: Record<string, string> = { discountId };
        if (deliveryMethod) params.deliveryMethod = deliveryMethod;
        if (shippingRateId) params.shippingRateId = shippingRateId;
        if (userAddressId) params.userAddressId = userAddressId;
        response = await globalAxios.get<CartResponseDto>(
          `${AXIOS_CONFIG.basePath}/cart/user/mine`,
          { params }
        );
      } else {
        response = await new CartApi(getApiConfiguration()).getUserCart(undefined, deliveryMethod, shippingRateId, undefined, userAddressId);
      }
      if (
        response.data &&
        requestId === cartFetchSequenceRef.current &&
        mutationVersion === cartMutationSequenceRef.current
      ) {
        commitCart(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      throw error;
    } finally {
      activeCartFetchesRef.current = Math.max(0, activeCartFetchesRef.current - 1);
      if (activeCartFetchesRef.current === 0) setIsFetching(false);
    }
  }, [isAuthenticated, calculateGuestCart, commitCart]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  // ─── Add to cart ──────────────────────────────────────────────────────────
  const addToCart = useCallback(async (productId: string, quantity: number = 1, variantId?: string) => {
    const runAdd = async () => {
      const targetVariantId = variantId || productId;
      // Invalidate any read that began before this mutation. A token can be
      // available briefly before AuthProvider has restored the cached user.
      cartMutationSequenceRef.current += 1;
      cartFetchSequenceRef.current += 1;
      const hasAuthenticatedSession = isAuthenticated || Boolean(getAuthToken());

      setIsLoading(true);
      try {
        if (!hasAuthenticatedSession) {
          // Persist first, but only announce success after the priced cart has
          // been returned and committed to visible state.
          addToGuestCart(targetVariantId, quantity);
          await calculateGuestCart();
          notification.success('Added to cart', 'The item was added to your cart.');
          return;
        }

        const currentCart = cartRef.current;
        const currentItems = currentCart?.cartBody?.items || [];

        let itemsFound = false;
        const simplifiedItems = currentItems.map((item: CartItemPopulated) => {
          let q = Number(item.quantity) || 0;
          if (String(item.productVariantId) === String(targetVariantId)) {
            itemsFound = true;
            q += Number(quantity);
          }
          return {
            _id: item._id,
            productVariantId: item.productVariantId,
            quantity: q
          };
        });

        if (!itemsFound) {
          simplifiedItems.push({
            productVariantId: targetVariantId,
            quantity: Number(quantity)
          } as any);
        }

        const payload = {
          _id: currentCart?.cartBody?._id,
          items: simplifiedItems
        };

        const response = await new CartApi(getApiConfiguration()).handleUserCart(payload as any);
        if (!response.data) throw new Error('The cart update returned no data.');

        commitCart(response.data);
        notification.success('Added to cart', 'The item was added to your cart.');
      } catch (error: any) {
        console.error('Add to cart error:', error);
        const errorMessage = error.response?.data?.message || '';

        const isExistingItemError = errorMessage.toLowerCase().includes('available') ||
          errorMessage.toLowerCase().includes('stock') ||
          errorMessage.toLowerCase().includes('quantity');

        await refreshCart();

        if (isExistingItemError) {
          const msg = 'Some items in your cart exceed available stock and have been adjusted.';
          notification.error('Stock limit reached', msg);
          setCartError(msg);
        } else {
          const msg = errorMessage || 'Could not add item to cart. Please try again.';
          notification.error('Could not add item', msg);
          setCartError(msg);
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    };

    const queuedAdd = addQueueRef.current.then(runAdd, runAdd);
    addQueueRef.current = queuedAdd.catch(() => undefined);
    return queuedAdd;
  }, [isAuthenticated, notification, refreshCart, calculateGuestCart, commitCart]);

  // ─── Update quantity ──────────────────────────────────────────────────────
  const updateQuantity = useCallback((productId: string, quantity: number) => {
    const targetQ = Number(quantity);

    if (!isAuthenticated) {
      // Guest: update localStorage immediately, debounce recalculate
      updateGuestCartItem(productId, targetQ);
      // Optimistic UI
      setCart(prev => {
        if (!prev) return null;
        const newItems = prev.cartBody.items.map(item => {
          if (String(item.productVariantId) === String(productId)) {
            return { ...item, quantity: targetQ };
          }
          return item;
        });
        const newSubtotal = Math.round(newItems.reduce((acc, item) => acc + (item.productVariantData?.finalPrice || 0) * item.quantity, 0) * 100) / 100;
        return { ...prev, subTotal: newSubtotal, total: newSubtotal + (prev.shipping || 0) + (prev.tax || 0), cartBody: { ...prev.cartBody, items: newItems } };
      });
      const existing = pendingUpdatesRef.current.get(productId);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        pendingUpdatesRef.current.delete(productId);
        calculateGuestCart().catch(() => {});
      }, 400);
      pendingUpdatesRef.current.set(productId, timer);
      return;
    }

    const currentCart = cartRef.current;
    if (!currentCart) return;

    // Optimistic UI Update immediately so the UI feels responsive
    setCart(prev => {
      if (!prev) return null;
      const newItems = prev.cartBody.items.map(item => {
        if (String(item.productVariantId) === String(productId)) {
          return { ...item, quantity: targetQ };
        }
        return item;
      });
      const newSubtotal = Math.round(newItems.reduce((acc, item) => acc + (item.productVariantData.finalPrice || 0) * item.quantity, 0) * 100) / 100;
      return {
        ...prev,
        subTotal: newSubtotal,
        total: newSubtotal + (prev.shipping || 0) + (prev.tax || 0),
        cartBody: { ...prev.cartBody, items: newItems }
      };
    });

    const existingTimer = pendingUpdatesRef.current.get(productId);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(async () => {
      pendingUpdatesRef.current.delete(productId);
      const snapshotCart = cartRef.current;
      if (!snapshotCart) return;

      setIsLoading(true);
      try {
        const simplifiedItems = snapshotCart.cartBody.items.map(item => ({
          _id: item._id,
          productVariantId: item.productVariantId,
          quantity: item.quantity
        }));

        const payload = { _id: snapshotCart.cartBody._id, items: simplifiedItems };
        const response = await new CartApi(getApiConfiguration()).handleUserCart(payload as any);

        if (response.data) {
          setCart(response.data);
        } else {
          await refreshCart();
        }
      } catch (error: any) {
        console.error('Update quantity error:', error);
        await refreshCart();
        const msg = error.response?.data?.message || 'Failed to update quantity. Please try again.';
        notification.error('Update failed', msg);
        setCartError(msg);
      } finally {
        setIsLoading(false);
      }
    }, 400);

    pendingUpdatesRef.current.set(productId, timer);
  }, [isAuthenticated, refreshCart, calculateGuestCart, notification]);

  // ─── Remove from cart ─────────────────────────────────────────────────────
  const removeFromCart = useCallback(async (productId: string) => {
    if (!isAuthenticated) {
      removeFromGuestCart(productId);
      setCart(prev => {
        if (!prev) return null;
        const newItems = prev.cartBody.items.filter(item => String(item.productVariantId) !== String(productId));
        if (newItems.length === 0) return null;
        const newSubtotal = Math.round(newItems.reduce((acc, item) => acc + (item.productVariantData?.finalPrice || 0) * item.quantity, 0) * 100) / 100;
        return { ...prev, subTotal: newSubtotal, total: newSubtotal + (prev.shipping || 0) + (prev.tax || 0), cartBody: { ...prev.cartBody, items: newItems } };
      });
      await calculateGuestCart();
      return;
    }

    const currentCart = cartRef.current;
    if (!currentCart) return;
    setIsLoading(true);
    const oldCart = currentCart;

    setCart(prev => {
      if (!prev) return null;
      const newItems = prev.cartBody.items.filter(item => String(item.productVariantId) !== String(productId));
      const newSubtotal = Math.round(newItems.reduce((acc, item) => acc + (item.productVariantData.finalPrice || 0) * item.quantity, 0) * 100) / 100;
      return {
        ...prev,
        subTotal: newSubtotal,
        total: newSubtotal + (prev.shipping || 0) + (prev.tax || 0),
        cartBody: { ...prev.cartBody, items: newItems }
      };
    });

    try {
      const itemsToKeep = currentCart.cartBody.items.filter(item => String(item.productVariantId) !== String(productId));
      const simplifiedItems = itemsToKeep.map(item => ({
        _id: item._id,
        productVariantId: item.productVariantId,
        quantity: item.quantity
      }));

      if (simplifiedItems.length === 0) {
        await new CartApi(getApiConfiguration()).clearCart();
        setCart(null);
      } else {
        const payload = { _id: currentCart.cartBody._id, items: simplifiedItems };
        const response = await new CartApi(getApiConfiguration()).handleUserCart(payload as any);
        if (response.data) {
          setCart(response.data);
        }
      }
    } catch (error: any) {
      console.error('Remove from cart error:', error);
      setCart(oldCart);
      notification.error('Could not remove item', error.response?.data?.message || 'Failed to remove item.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, notification, calculateGuestCart]);

  // ─── Clear cart ───────────────────────────────────────────────────────────
  const clearCart = useCallback(async () => {
    // Always clear localStorage guest cart first (synchronous, prevents merge race)
    clearGuestCart();
    setCart(null);

    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      await new CartApi(getApiConfiguration()).clearCart();
    } catch (error: any) {
      console.error('Clear cart error:', error);
      notification.error('Could not clear cart', error.response?.data?.message || 'Failed to clear cart.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, notification]);

  // ─── Merge guest cart on login ────────────────────────────────────────────
  const mergeGuestCart = useCallback(async () => {
    const guestItems = readGuestCart();
    if (guestItems.length === 0) return;

    cartMutationSequenceRef.current += 1;
    cartFetchSequenceRef.current += 1;
    try {
      // Fetch existing server cart
      let serverItems: Array<{ productVariantId: string; quantity: number }> = [];
      try {
        const res = await new CartApi(getApiConfiguration()).getUserCart();
        serverItems = (res.data?.cartBody?.items || []).map((item: CartItemPopulated) => ({
          productVariantId: String(item.productVariantId),
          quantity: Number(item.quantity) || 0,
        }));
      } catch {
        // Server cart may be empty — that's fine
      }

      // Merge: add guest quantities to existing items, append new items
      const merged = [...serverItems];
      for (const guestItem of guestItems) {
        const existing = merged.find(i => i.productVariantId === guestItem.productVariantId);
        if (existing) {
          existing.quantity += guestItem.quantity;
        } else {
          merged.push({ productVariantId: guestItem.productVariantId, quantity: guestItem.quantity });
        }
      }

      const res = await new CartApi(getApiConfiguration()).handleUserCart({ items: merged } as any);
      if (res.data) commitCart(res.data);
      clearGuestCart();
    } catch (error: any) {
      console.error('Failed to merge guest cart:', error);
      // Keep the local cart so a temporary network/auth failure cannot discard
      // items the customer added before signing in.
    }
  }, [commitCart]);

  // ─── Reorder from order ───────────────────────────────────────────────────
  const reorderFromOrder = useCallback(async (order: PopulatedOrder) => {
    if (!isAuthenticated) return;
    if (!order.items || order.items.length === 0) {
      notification.warning('No items to reorder', 'This order has no items to add to cart.');
      return;
    }
    setIsLoading(true);
    try {
      const currentCart = cartRef.current;
      const currentItems = currentCart?.cartBody?.items || [];

      const simplifiedItems = currentItems.map((item: CartItemPopulated) => ({
        _id: item._id,
        productVariantId: item.productVariantId,
        quantity: item.quantity
      }));

      for (const orderItem of order.items) {
        const variantId = orderItem.productVariantId;
        if (!variantId) continue;

        const existingIndex = simplifiedItems.findIndex(
          item => String(item.productVariantId) === String(variantId)
        );

        if (existingIndex >= 0) {
          simplifiedItems[existingIndex].quantity += orderItem.quantity;
        } else {
          simplifiedItems.push({ productVariantId: variantId, quantity: orderItem.quantity } as any);
        }
      }

      const payload = { _id: currentCart?.cartBody?._id, items: simplifiedItems };
      const response = await new CartApi(getApiConfiguration()).handleUserCart(payload as any);
      if (response.data) {
        setCart(response.data);
        notification.success('Items added to cart', `${order.items.length} item(s) from your order have been added to your cart.`);
      }
    } catch (error: any) {
      console.error('Reorder error:', error);
      notification.error('Could not reorder', error.response?.data?.message || 'Failed to add items to cart.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, notification]);

  // ─── Save for later ───────────────────────────────────────────────────────
  const saveForLater = useCallback(async (item: CartItemPopulated) => {
    if (!isAuthenticated) {
      notification.error('Sign-in required', 'Please sign in to save items for later.');
      return;
    }

    const productId =
      (item as any).productId ||
      (item as any).product?._id ||
      (item as any).product?.id ||
      (item.productVariantData as any).productId ||
      (item.productVariantData as any).product?._id ||
      (item.productVariantData as any).product?.id ||
      item.productVariantData?._id ||
      item.productVariantData?.id;

    if (!productId) {
      notification.error('Cannot save item', 'Unable to identify the product.');
      return;
    }

    const product: Product = {
      _id: productId,
      id: productId,
      name: item.productVariantData?.name || 'Product',
    } as unknown as Product;

    setIsLoading(true);
    try {
      const wasAdded = await addToWishlist(product);
      await removeFromCart(item.productVariantId);
      if (wasAdded) {
        notification.success('Saved for later', 'Item moved to your wishlist for future purchase.');
      } else {
        notification.info('Already in wishlist', 'Item removed from cart (it was already in your wishlist).');
      }
    } catch (error: any) {
      console.error('Save for later error:', error);
      notification.error('Could not save for later', error.response?.data?.message || 'Failed to move item to wishlist.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, notification, removeFromCart, addToWishlist]);

  const value = {
    cart,
    isLoading,
    isFetching,
    cartError,
    clearCartError,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart,
    reorderFromOrder,
    saveForLater,
    mergeGuestCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
